// coex-cert.js — sign / verify a coexistence authorization certificate with the
// platform's Web Crypto (SubtleCrypto), keyed to the authenticated session.
//
// Honesty boundary (matches the wasm engine + glossary): the engine
// (run_coexistence_certified) produces a deterministic, reproducible decision
// payload + an xxhash CONTENT fingerprint (dedup/reproducibility, NOT security).
// This module applies the cryptographic signature. HMAC-SHA256 with a key derived
// from the auth token gives ATTRIBUTABLE provenance for this authenticated
// session — it binds the decision to the session, tamper-evidently. It is NOT
// non-repudiable against an adversary who can read the session key; for that the
// signing key must be server-held or the receipt server-co-signed. We do not
// roll our own crypto — SubtleCrypto is the vetted, hardware-accelerated path.

async function importHmacKey(authToken) {
  const raw = new TextEncoder().encode(String(authToken || ''));
  return crypto.subtle.importKey('raw', raw, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign', 'verify']);
}

function b64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function unb64(s) {
  return Uint8Array.from(atob(s), c => c.charCodeAt(0));
}

/// Sign a certificate (the object returned by run_coexistence_certified). The
/// signed bytes are `signing_payload` + the timestamp, so the time is covered by
/// the signature. Returns a new object with `signature`, `signed_at`, algo.
export async function signReceipt(receipt, authToken) {
  const signed_at = new Date().toISOString();
  const bytes = new TextEncoder().encode(`${receipt.signing_payload}|signed_at=${signed_at}`);
  const key = await importHmacKey(authToken);
  const sig = await crypto.subtle.sign('HMAC', key, bytes);
  return {
    ...receipt,
    signed_at,
    signature: b64(sig),
    signature_algo: 'HMAC-SHA256 (session-attributable; not non-repudiable — server co-sign for that)',
  };
}

/// Verify a signed certificate against the session auth token. Returns boolean.
export async function verifyReceipt(signed, authToken) {
  if (!signed || !signed.signature || !signed.signing_payload || !signed.signed_at) return false;
  const bytes = new TextEncoder().encode(`${signed.signing_payload}|signed_at=${signed.signed_at}`);
  const key = await importHmacKey(authToken);
  return crypto.subtle.verify('HMAC', key, unb64(signed.signature), bytes);
}
